/**
 * Print an item's label straight from this device (ARCHITECTURE.md §6): draw,
 * encode for the printer, guard, send over Web Bluetooth or Web Serial. No
 * server is in the path. Where the browser has neither, it says so; the phone
 * app prints over BLE.
 */
import { useEffect, useRef, useState } from "react";
import { PRINTERS, printLabel, type Connection } from "@sable/label";
import {
  canWebBluetooth, canWebSerial, rememberedWebBluetooth, requestWebBluetooth, requestWebSerial,
} from "@sable/label/web";
import type { ItemDetail } from "@sable/api-contract";
import { t } from "../i18n/index.ts";
import { drawLabel } from "../lib/labelArt.ts";
import { Dialog, Field } from "./ui.tsx";

// One connection per page load; a paired printer is reused for the next label.
let connection: Connection | null = null;

export function LabelDialog({ item, onClose }: { item: ItemDetail; onClose: () => void }) {
  const model = PRINTERS[0]!;
  const [mediaId, setMediaId] = useState(model.media[0]!.id);
  const media = model.media.find((m) => m.id === mediaId)!;
  const [line, setLine] = useState((item.brand ?? "").trim().toUpperCase());
  const [size, setSize] = useState((item.sizeLabel ?? "").trim().toUpperCase());
  const [copies, setCopies] = useState(1);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const direct = canWebBluetooth || canWebSerial;

  useEffect(() => {
    if (canvas.current) drawLabel(canvas.current, media, model.dpi, item.sku, line.trim(), size.trim());
  }, [media, model.dpi, item.sku, line, size]);

  useEffect(() => {
    if (!connection && canWebBluetooth) {
      void rememberedWebBluetooth(model, () => { connection = null; }).then((tr) => {
        if (tr) connection = { model, transport: tr };
      });
    }
  }, [model]);

  async function print(via: "connected" | "bluetooth" | "serial") {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      // Pick the device first, straight off the tap: browsers only show the picker then.
      if (via !== "connected" || !connection) {
        const transport = via === "serial"
          ? await requestWebSerial(model)
          : await requestWebBluetooth(model, false, () => { connection = null; });
        connection = { model, transport };
      }
      const c = canvas.current!;
      const pixels = c.getContext("2d")!.getImageData(0, 0, c.width, c.height);
      await printLabel(connection, media, pixels, copies);
      setStatus(t("label.printed"));
    } catch (err) {
      if (err instanceof DOMException && err.name === "NotFoundError") return; // picker closed
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={t("label.title")}
      onClose={onClose}
      footer={direct ? (
        <>
          {canWebSerial && <button className="btn" disabled={busy} onClick={() => void print("serial")}>{t("label.connectSerial")}</button>}
          {connection
            ? <button className="btn btn-primary" disabled={busy} onClick={() => void print("connected")}>{t("label.print")}</button>
            : canWebBluetooth && <button className="btn btn-primary" disabled={busy} onClick={() => void print("bluetooth")}>{t("label.connect")}</button>}
        </>
      ) : undefined}
    >
      <div className="stack">
        <canvas ref={canvas} className="label-preview" aria-label={item.sku} />
        <div className="grid-2">
          <Field label={t("item.description")}>
            <input className="input" value={line} maxLength={60} onChange={(e) => setLine(e.target.value)} />
          </Field>
          <Field label={t("intake.size")}>
            <input className="input" value={size} maxLength={12} onChange={(e) => setSize(e.target.value)} />
          </Field>
          <Field label={t("label.size")}>
            <select className="select" value={mediaId} onChange={(e) => setMediaId(e.target.value)}>
              {model.media.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </Field>
          <Field label={t("label.copies")}>
            <input className="input" type="number" min={1} max={20} value={copies} onChange={(e) => setCopies(Math.min(20, Math.max(1, Number(e.target.value) || 1)))} />
          </Field>
        </div>
        <p className="muted small">{t("label.printer")}: {model.name}</p>
        {!direct && <div className="banner">{t("label.unsupported")}</div>}
        {status && <div className="banner banner-ok" role="status">{status}</div>}
        {error && <div className="banner banner-error" role="alert">{error}</div>}
      </div>
    </Dialog>
  );
}
